import React, {useMemo} from 'react'
import { Button } from 'antd'
import moment from 'moment'
import { IBestDataset } from '../../../../../../types/analytics'
import { IEnrichedMetadata } from '../../../../../../types/metadata'
import { IAdvancedAnalyticsApiPayload } from '../../../../../../types/chart'
import { IAnalytcisUserInput } from '../../../../../../types/IAnalyticsUserInput'
import { MarkerItem } from '../../../../../../types/home'
import { useTidalParameters } from "../../../hooks/useTidalParameters"
import { AnalyticsSection } from "../../Layout/AnalyticsSection"
import TidalLevelInputForm from "./TidalLevelInputForm"
import TidalLevelChart from "./TidalLevelChart"
import TidalLevelAdditionalContent from "./TidalLevelAdditionalContent";

interface TidalLevelProps {
  coordinates: number[]
  previewDatasetId: string
  datasets: IBestDataset[]
  onDatasetChange: (item: string) => void
  dateRange: [moment.Moment, moment.Moment]
  enrichedMetadata: IEnrichedMetadata[]
  onUpdate: (payload: IAdvancedAnalyticsApiPayload) => void
  tidalLevelValueImage: string
  tidalValueLinks: string[]
  error: string
  updating: boolean
  pointName: string
  userInput: IAnalytcisUserInput
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>
  point?: MarkerItem
  onChangeActiveKey: (item: any) => void
  pointOption: { label: string, value: string }[]
  globalDateRange: any[]
  setGlobalDateRange: (item: any) => void
  isForAllAnalytics: boolean
  setIsForAllAnalytics: (item: any) => void
  isOfflineDataset: boolean
}

export default function TidalLevel(props: TidalLevelProps) {
  const {
    tidalLevelValueImage,
    tidalValueLinks,
    error,
    updating,
    isOfflineDataset
  } = props

  const {
    listVariablesX,
    variableX,
    startDate,
    endDate,
    supported,
    categories,
    onUpdateTidalLevel,
    doVariableChange,
    doStartDateChange,
    doEndDateChange,
    onSelectDataset,
    handleCheckboxChange,
    disabledStartDate,
    disabledEndDate
  } = useTidalParameters(props)

  // TODO: enable cache 
  // const upsertTidalLevelCache = useAnalyticsCacheStore(state => state.upsertTidalLevelCache)

  // useEffect(() => {
  //   if (!props.previewDatasetId || !variableX?.Index) return

  //   const key = `${props.previewDatasetId}_${variableX.Index}_${props.coordinates[0]},${props.coordinates[1]}`

  //   upsertTidalLevelCache(key, {
  //     variableXIndex: variableX.Index,
  //     startDate,
  //     endDate,
  //     isForAllAnalytics: props.isForAllAnalytics,
  //     tidalLevelValueImage,
  //     tidalValueLinks
  //   })
  // }, [upsertTidalLevelCache, props.previewDatasetId, variableX?.Index, startDate, endDate, props.coordinates, props.isForAllAnalytics, tidalLevelValueImage, tidalValueLinks]);

  const plotInputContent = useMemo(() => (
    <TidalLevelInputForm
      datasets={props.datasets}
      dataset={props.previewDatasetId}
      onSelectDataset={onSelectDataset}
      categories={categories}
      variables={listVariablesX}
      variableX={variableX}
      onVariableChange={doVariableChange}
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={doStartDateChange}
      onEndDateChange={doEndDateChange}
      disabledStartDate={disabledStartDate}
      disabledEndDate={disabledEndDate}
      isForAllAnalytics={props.isForAllAnalytics}
      onCheckboxChange={handleCheckboxChange}
      supported={supported}
      isLoading={updating}
      isOfflineDataset={isOfflineDataset}
    />
  ), [
    props.datasets,
    props.previewDatasetId,
    onSelectDataset,
    categories,
    listVariablesX,
    variableX,
    doVariableChange,
    startDate,
    endDate,
    doStartDateChange,
    doEndDateChange,
    disabledStartDate,
    disabledEndDate,
    props.isForAllAnalytics,
    handleCheckboxChange,
    supported,
    updating,
    error
  ])

  const updateContent = useMemo(() => (
    <div style={{
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '36px'
    }}>
      <Button
        type="primary"
        id="Analytics-TidalLevel-Update_Button"
        disabled={updating || !supported || isOfflineDataset}
        loading={updating}
        onClick={() => onUpdateTidalLevel()}
        style={{
          width: '90px',
          height: '36px'
        }}
      >
        Update
      </Button>
    </div>
  ), [
    updating,
    supported,
    onUpdateTidalLevel
  ])

  const chartContent = useMemo(() => (
    <TidalLevelChart
      tidalLevelValueImage={tidalLevelValueImage}
      tidalValueLinks={tidalValueLinks}
      supported={supported}
      isLoading={updating}
      error={error}
      isOfflineDataset={isOfflineDataset}
    />
  ), [
    tidalLevelValueImage,
    tidalValueLinks,
    supported,
    updating,
    error
  ])

  const additionalContent = useMemo(() => (
    <TidalLevelAdditionalContent />
  ), [])

  return (
    <AnalyticsSection
      plotInputContent={plotInputContent}
      updateContent={updateContent}
      chartContent={chartContent}
      additionalContent={additionalContent}
      isLoading={updating}
    />
  )
}
